import {
  FormControl,
  IconButton,
  ListItemText,
  Radio,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import {
  CheckCircleOutline,
  Close,
  InfoOutlined,
  MoreVert,
  ReportProblemOutlined,
} from '@material-ui/icons'
import { useEffect, useState } from 'react'

import AlertDialog from '@/components/alert-dialog'
import { useRemoveMember } from '@/hooks/mutations/use-remove-member'
import { useUpdateUserRole } from '@/hooks/mutations/use-update-user-role'
import { useCompanies } from '@/hooks/use-companies'

import { useStyles } from './styles'

interface User {
  id: number
  name: string
  email: string
  role: 'admin' | 'owner' | 'member'
}

interface MembersTableProps {
  members?: User[]
}

const roleMap = {
  owner: 'Proprietário(a)',
  admin: 'Administrador(a)',
  member: 'Membro',
}

export function MembersTable({ members }: MembersTableProps) {
  const classes = useStyles()

  const [menu, setMenu] = useState<null | HTMLElement>(null)
  const [isRemoveAlertOpen, setIsRemoveAlertOpen] = useState(false)
  const [isChangeRoleAlertOpen, setIsChangeRoleAlertOpen] = useState(false)
  const [selectedRemoveUser, setSelectedRemoveUser] = useState(0)
  const [selectedChangeRoleUser, setSelectedChangeRoleUser] = useState<{
    userId: number
    role: string
  }>()

  const { selectedCompany, permissions } = useCompanies()
  const removeMemberMutation = useRemoveMember()
  const updateUserRoleMutation = useUpdateUserRole()

  useEffect(() => {
    return () => {
      if (!isRemoveAlertOpen && !removeMemberMutation.isIdle) {
        removeMemberMutation.reset()
      }
    }
  }, [isRemoveAlertOpen])

  useEffect(() => {
    return () => {
      if (!isChangeRoleAlertOpen && !updateUserRoleMutation.isIdle) {
        updateUserRoleMutation.reset()
      }
    }
  }, [isChangeRoleAlertOpen])

  const handleChangeMemberAccess = (role: string, userId: number) => {
    setSelectedChangeRoleUser({ userId, role })
    setIsChangeRoleAlertOpen(true)
  }

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    userId: number,
  ) => {
    setMenu(event.currentTarget)
    setSelectedRemoveUser(userId)
  }

  const handleCloseMenu = () => {
    setMenu(null)
    setSelectedRemoveUser(0)
  }

  const handleRemoveConfirm = () => {
    removeMemberMutation.mutate({
      userId: selectedRemoveUser,
      companyId: selectedCompany.id,
    })
  }

  const getRemoveAlertProps = () => {
    if (removeMemberMutation.isSuccess) {
      return {
        icon: <CheckCircleOutline />,
        title: 'Membro Removido',
        subtitle: 'O membro foi removido desta unidade.',
        items: [],
        variant: 'success' as const,
        confirm: 'Ok, entendi',
        handleConfirm: () => {
          setIsRemoveAlertOpen(false)
        },
      }
    }

    if (removeMemberMutation.isError) {
      return {
        icon: <InfoOutlined />,
        title: 'Ocorreu um erro',
        subtitle: 'Não foi possível remover este membro',
        items: ['Esta conta não tem permissão para remover um membro'],
        variant: 'error' as const,
        confirm: 'Ok, entendi',
        handleConfirm: () => {
          setIsRemoveAlertOpen(false)
        },
      }
    }

    return {
      icon: <ReportProblemOutlined />,
      title: 'Remover Membro',
      subtitle: 'Deseja remover o membro da unidade?',
      items: [
        'O usuário será removido da companhia',
        'O usuário perderá todos os conteúdos desta unidade',
      ],
      variant: 'warning' as const,
      confirm: 'Sim, remover usuário',
      handleConfirm: handleRemoveConfirm,
      handleCancel: () => {
        setIsRemoveAlertOpen(false)
      },
    }
  }

  const getChangeRoleAlertProps = () => {
    if (updateUserRoleMutation.isError) {
      return {
        icon: <InfoOutlined />,
        title: 'Ocorreu um erro',
        subtitle: 'Não conseguimos alterar este acesso',
        items: ['Tente novamente mais tarde ou entre em contato com a Cromai.'],
        variant: 'error' as const,
        confirm: 'Ok, entendi',
        handleConfirm: () => {
          setIsChangeRoleAlertOpen(false)
        },
      }
    }

    return {
      icon: <ReportProblemOutlined />,
      title: 'Alterar nível de acesso',
      subtitle: 'Deseja alterar o nível de acesso deste membro?',
      items: [
        'A Permissão do usuário será alterada.',
        'Apenas o proprietário poderá realizar uma segunda mudança.',
      ],
      variant: 'warning' as const,
      confirm: 'Sim, alterar',
      handleConfirm: () => {
        if (selectedChangeRoleUser) {
          updateUserRoleMutation.mutate(
            {
              companyId: selectedCompany.id,
              userId: selectedChangeRoleUser.userId,
              role: selectedChangeRoleUser.role,
            },
            { onSuccess: () => setIsChangeRoleAlertOpen(false) },
          )
        }
      },
      handleCancel: () => {
        setIsChangeRoleAlertOpen(false)
      },
    }
  }

  return (
    <>
      <TableContainer className={classes.container}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant='h3' color='textSecondary'>
                  Nome
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='h3' color='textSecondary'>
                  Email
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='h3' color='textSecondary'>
                  Acesso
                </Typography>
              </TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members?.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <FormControl className={classes.select} variant='filled'>
                    <Select
                      value={user.role}
                      onChange={(event) =>
                        handleChangeMemberAccess(
                          event.target.value as string,
                          user.id,
                        )
                      }
                      renderValue={(selected) =>
                        roleMap[selected as keyof typeof roleMap]
                      }
                      MenuProps={{
                        className: classes.selectMenu,
                        transformOrigin: {
                          vertical: -48,
                          horizontal: 'left',
                        },
                      }}
                      disableUnderline
                      disabled={!permissions?.updateMembers}
                      defaultValue=''
                    >
                      <MenuItem value={'owner'}>
                        <Radio checked={user.role === 'owner'} size='small' />
                        <ListItemText primary='Proprietário' />
                      </MenuItem>
                      <MenuItem value={'admin'}>
                        <Radio checked={user.role === 'admin'} size='small' />
                        <ListItemText primary='Administrador' />
                      </MenuItem>
                      <MenuItem value={'member'}>
                        <Radio checked={user.role === 'member'} size='small' />
                        <ListItemText primary='Membro' />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell align='right'>
                  <IconButton
                    aria-label={`Opções do membro ${user.id}`}
                    onClick={(event) => handleOpenMenu(event, user.id)}
                    size='small'
                  >
                    <MoreVert />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menu}
        open={Boolean(menu)}
        onClose={handleCloseMenu}
        classes={{ paper: classes.menu }}
        transformOrigin={{
          vertical: 10,
          horizontal: 160,
        }}
      >
        <MenuItem onClick={handleCloseMenu} aria-label='Fechar menu'>
          <Close fontSize='inherit' />
        </MenuItem>
        <MenuItem
          onClick={() => {
            setIsRemoveAlertOpen(true)
            setMenu(null)
          }}
        >
          <span className='material-symbols-outlined'>person_remove</span>
          Remover Membro
        </MenuItem>
      </Menu>

      <AlertDialog
        {...getRemoveAlertProps()}
        open={isRemoveAlertOpen}
        loading={removeMemberMutation.isLoading}
      />
      <AlertDialog
        {...getChangeRoleAlertProps()}
        open={isChangeRoleAlertOpen}
        loading={updateUserRoleMutation.isLoading}
      />
    </>
  )
}
